'use client';

import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import posthog from 'posthog-js';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { useUploadStore, type ActiveUpload } from '@/shared/stores/upload-store';
import { useUploadHistoryStore } from '@/shared/stores/upload-history-store';

const ABANDONABLE_PHASES: ReadonlyArray<ActiveUpload['phase']> = [
  'preparing',
  'uploading',
  'processing',
];

/**
 * Disambiguates "no terminal event" rows in dashboard tiles #11 and #12: a
 * matching studio_upload_abandoned for the upload_id means the user closed
 * the tab — not a stuck upload. No matching event ⇒ real FE/BE/Bunny bug.
 *
 * pagehide is the right unload signal (Safari back-swipe, mobile). Default
 * capture() only enqueues into the batching queue, whose unload flush is
 * best-effort and routinely loses events (confirmed 2026-08-13: abandoned
 * event lost while posthog's own beacon-sent $pageleave arrived). The
 * sendBeacon + send_instantly options bypass the queue entirely.
 */
export function useAbandonedUploadReporter() {
  const uploads = useUploadStore(useShallow((state) => state.uploads));
  const uploadsRef = useRef(uploads);

  useEffect(() => {
    uploadsRef.current = uploads;
  }, [uploads]);

  useEffect(() => {
    const handler = () => {
      const { markAbandoned } = useUploadHistoryStore.getState();
      for (const upload of uploadsRef.current.values()) {
        if (upload.isStale) continue;
        if (!ABANDONABLE_PHASES.includes(upload.phase)) continue;
        markAbandoned(upload.id, Date.now());
        posthog.capture(
          AnalyticsEvent.STUDIO_UPLOAD_ABANDONED,
          {
            stage: upload.phase,
            upload_id: upload.id,
            video_uuid: upload.videoUuid,
            bunny_video_id: upload.bunnyVideoId,
            bytes_uploaded: upload.bytesUploaded,
            total_bytes: upload.bytesTotal,
            progress: upload.progress,
            // The modal writes publishMode to the store before calling publish(),
            // so a tab closed during encoding with mode auto/scheduled is the
            // designed "publish when ready" flow, not a drop-off.
            publish_requested: upload.metadata.publishMode !== 'none',
            publish_mode: upload.metadata.publishMode,
          },
          { transport: 'sendBeacon', send_instantly: true }
        );
      }
    };

    window.addEventListener('pagehide', handler);
    return () => window.removeEventListener('pagehide', handler);
  }, []);
}
